import Footer from "components/Footer";
import { AddOrgModal } from "components/Modals/AddOrgModel";
import { isOrgAdminRole } from "utils/roleUtils";
import { safeLocalStorageSetItem } from "utils/localStorage";
import { BillingContext } from "contexts/BillingContext";
import { DarkModeContext } from "contexts/DarkModeContext";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { UserContext } from "contexts/UserContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { ProfileDropDown } from "components/DashboardContainer/ProfileDropDown";
import InfoBanner from "components/DashboardContainer/InfoBanner";
import { useEnvironmentBurnRateBreakdown } from "components/Environment/utils";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  formatGrantHeadline,
  formatProgramDaysLeft,
  formatProgramEndDate,
  getProgramDaysRemaining,
  grantAccessTone,
  shouldShowAcademicGrantAccess,
} from "components/utils";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";
import { buildOrganizationSettingsTabHref } from "components/OrgSettings/settingsTabs";

import ApplicationBalance from "../ApplicationBalance";
import { buildApplicationBalanceModel } from "../ApplicationBalance/model";
import ApplicationShell from "../ApplicationShell";
import BrandLockup from "../BrandLockup";
import PrimaryIconRail from "../PrimaryIconRail";
import ProductTopBar from "../ProductTopBar";
import WorkspaceContextBreadcrumb from "../WorkspaceContextBreadcrumb";
import type { ShellNavItem, ShellOrganization } from "../types";
import {
  buildApplicationNavigation,
  getActiveApplicationNavigationItemId,
  getOrganizationSwitchHref,
} from "../navigation";
import AppShellBillingModal from "./AppShellBillingModal";
import { getApplicationEnvironmentPresentation } from "../environmentPresentation";

export const APPLICATION_SHELL_NAVIGATION_STORAGE_KEY =
  "brev:application-shell:navigation-expanded";

const readStoredNavigationState = () => {
  if (typeof window === "undefined") return true;

  try {
    return (
      window.localStorage.getItem(APPLICATION_SHELL_NAVIGATION_STORAGE_KEY) !==
      "collapsed"
    );
  } catch {
    return true;
  }
};

export const getBillingActivationHref = (activeOrgId: string) =>
  activeOrgId
    ? buildOrganizationSettingsTabHref(
        activeOrgId,
        "billing",
        "?openAddCredits=true",
      )
    : "/org/billing?openAddCredits=true";

export interface AuthenticatedAppShellProps {
  children: React.ReactNode;
}

export const AuthenticatedAppShell: React.FC<AuthenticatedAppShellProps> = ({
  children,
}) => {
  const history = useHistory();
  const location = useLocation();
  const orgContext = React.useContext(OrgContext);
  const instanceTypesContext = React.useContext(InstanceTypesContext);
  const workspaceContext = React.useContext(WorkspaceContext);
  const userContext = React.useContext(UserContext);
  const billingContext = React.useContext(BillingContext);
  const oneClickDeployContext = React.useContext(OneClickDeployContext);
  const { darkMode, toggleDarkMode } = React.useContext(DarkModeContext);
  const [navigationExpanded, setNavigationExpanded] = React.useState(
    readStoredNavigationState,
  );
  const [showAddOrganizationModal, setShowAddOrganizationModal] =
    React.useState(false);
  const environmentPresentation = getApplicationEnvironmentPresentation(
    import.meta.env.MODE,
  );

  const isAdmin = isOrgAdminRole(orgContext.activeOrgRole);
  const includeTeam = !orgContext.isOrgAccessLoading && isAdmin;
  const canViewBilling = !orgContext.isOrgAccessLoading && isAdmin;
  const includeSecrets =
    useIdentityFeatureFlagEnabled(POSTHOG_IDENTITY_FLAGS.BREV_SECRETS_UI) ===
    true;
  const { primarySections, primaryItems, footerItems } = React.useMemo(
    () =>
      buildApplicationNavigation({
        activeOrgId: orgContext.activeOrgId,
        includeSecrets,
        includeTeam,
      }),
    [includeSecrets, includeTeam, orgContext.activeOrgId],
  );
  const activeItemId = getActiveApplicationNavigationItemId(
    location.pathname,
    location.search,
  );

  const burnBreakdown = useEnvironmentBurnRateBreakdown(
    workspaceContext.environments,
  );

  const showGrantAccess = shouldShowAcademicGrantAccess(
    orgContext.activeOrg,
    instanceTypesContext.currencyDisplay,
  );
  const grantAccess = React.useMemo(() => {
    if (!showGrantAccess) return null;
    const grantDaysRemaining = getProgramDaysRemaining(orgContext.activeOrg);
    return {
      amount: formatProgramDaysLeft(orgContext.activeOrg) || "—",
      headline: formatGrantHeadline(
        formatProgramEndDate(orgContext.activeOrg),
        grantDaysRemaining,
      ),
      tone: grantAccessTone(grantDaysRemaining),
    };
  }, [orgContext.activeOrg, showGrantAccess]);

  const applicationBalanceModel = React.useMemo(
    () =>
      buildApplicationBalanceModel({
        currencyDisplay: instanceTypesContext.currencyDisplay,
        currentBalance: billingContext.currentBalance,
        gpuHoursLeastExpensive: billingContext.gpuHoursLeastExpensive,
        gpuHoursMostExpensive: billingContext.gpuHoursMostExpensive,
        leastExpensiveInstanceType:
          instanceTypesContext.leastExpensiveAvailableInstance?.type,
        mostExpensiveInstanceType:
          instanceTypesContext.mostExpensiveAvailableInstance?.type,
        burnBreakdown,
        isDataLoading:
          userContext.isLoading ||
          orgContext.isLoading ||
          !orgContext.activeOrgId ||
          billingContext.isBalanceLoading,
        isWorkspaceLoading:
          !billingContext.isBalanceLoading && workspaceContext.isLoading,
        isGpuHoursLoading:
          !showGrantAccess &&
          instanceTypesContext.currencyDisplay === "hours" &&
          instanceTypesContext.isInstancesLoading,
        grantAccess,
      }),
    [
      billingContext.currentBalance,
      billingContext.gpuHoursLeastExpensive,
      billingContext.gpuHoursMostExpensive,
      billingContext.isBalanceLoading,
      burnBreakdown,
      orgContext.activeOrgId,
      instanceTypesContext.currencyDisplay,
      instanceTypesContext.isInstancesLoading,
      orgContext.isLoading,
      instanceTypesContext.leastExpensiveAvailableInstance?.type,
      instanceTypesContext.mostExpensiveAvailableInstance?.type,
      grantAccess,
      showGrantAccess,
      userContext.isLoading,
      workspaceContext.isLoading,
    ],
  );

  const organizations = React.useMemo<ShellOrganization[]>(
    () =>
      orgContext.orgs.map((organization) => ({
        id: organization.id,
        name: organization.name,
        slug: organization.slug,
      })),
    [orgContext.orgs],
  );

  const handleNavigate = (item: ShellNavItem) => {
    if (item.disabled) return;
    if (item.external) {
      window.open(item.href, "_blank", "noopener,noreferrer");
      return;
    }
    history.push(item.href);
  };

  const handleOrganizationChange = (organization: ShellOrganization) => {
    if (organization.id === orgContext.activeOrgId) return;

    oneClickDeployContext.reset();
    workspaceContext.clear();
    orgContext.setActiveOrgId(organization.id);

    const nextHref = getOrganizationSwitchHref(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      organization.id,
    );
    if (nextHref) history.push(nextHref);
  };

  const handleNavigationExpandedChange = (expanded: boolean) => {
    setNavigationExpanded(expanded);
    safeLocalStorageSetItem(
      APPLICATION_SHELL_NAVIGATION_STORAGE_KEY,
      expanded ? "expanded" : "collapsed",
    );
  };

  const navigation = (
    <PrimaryIconRail
      items={primaryItems}
      sections={primarySections}
      activeItemId={activeItemId}
      footerItems={footerItems}
      onNavigate={handleNavigate}
      expanded={navigationExpanded}
      collapsible
      onExpandedChange={handleNavigationExpandedChange}
      footer={
        <ProfileDropDown
          me={userContext.me}
          history={history}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          userContext={userContext}
          className="ml-0 w-full"
          triggerClassName={
            navigationExpanded
              ? "rounded-sm px-2"
              : "justify-center rounded-sm p-0"
          }
          contentAlign="start"
          contentSide={navigationExpanded ? "top" : "right"}
          variant={navigationExpanded ? "account-row" : "avatar"}
        />
      }
    />
  );

  const mobileNavigation = (
    <PrimaryIconRail
      items={primaryItems}
      sections={primarySections}
      activeItemId={activeItemId}
      footerItems={footerItems}
      ariaLabel="Mobile product navigation"
      onNavigate={handleNavigate}
      mobile
      footer={
        <ProfileDropDown
          me={userContext.me}
          history={history}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          userContext={userContext}
          className="ml-0"
          contentAlign="end"
          contentSide="top"
        />
      }
    />
  );

  return (
    <>
      <AppShellBillingModal />
      <AddOrgModal
        isOpen={showAddOrganizationModal}
        setOpen={setShowAddOrganizationModal}
        onSuccess={() => {
          oneClickDeployContext.reset();
          setShowAddOrganizationModal(false);
        }}
        onCancel={() => setShowAddOrganizationModal(false)}
      />
      <ApplicationShell
        railWidth={navigationExpanded ? "224px" : "64px"}
        header={
          <ProductTopBar
            environmentTone={environmentPresentation?.tone}
            brand={
              <button
                type="button"
                aria-label="Go to environments"
                className="flex items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-highlight/40"
                onClick={() =>
                  history.push(
                    orgContext.activeOrgId
                      ? `/org/${orgContext.activeOrgId}/environments`
                      : "/org/environments",
                  )
                }
              >
                <BrandLockup
                  logo={
                    <img
                      src="/logos/svg/nvidia-white-text.svg"
                      alt=""
                      draggable={false}
                    />
                  }
                  ariaLabel="NVIDIA"
                />
              </button>
            }
            workspaceContext={
              <WorkspaceContextBreadcrumb
                organizations={organizations}
                activeOrganizationId={orgContext.activeOrgId}
                onOrganizationChange={handleOrganizationChange}
                onCreateOrganization={() => setShowAddOrganizationModal(true)}
                onManageOrganization={() =>
                  history.push(
                    orgContext.activeOrgId
                      ? `/org/${orgContext.activeOrgId}/settings`
                      : "/org/settings",
                  )
                }
              />
            }
            balance={
              canViewBilling ? (
                <ApplicationBalance
                  model={applicationBalanceModel}
                  onActivate={() =>
                    history.push(
                      getBillingActivationHref(orgContext.activeOrgId),
                    )
                  }
                />
              ) : undefined
            }
            utilities={[]}
            onUtilityAction={() => undefined}
          />
        }
        navigation={navigation}
        mobileNavigation={mobileNavigation}
        mainAriaLabel="Brev application workspace"
      >
        <div className="flex min-h-full flex-col bg-[#070a08]">
          <InfoBanner />
          <div
            className="relative min-w-0 w-full flex-1"
            data-application-route-content
          >
            {children}
          </div>
          <Footer />
        </div>
      </ApplicationShell>
    </>
  );
};

AuthenticatedAppShell.displayName = "AuthenticatedAppShell";

export default AuthenticatedAppShell;
